import i18n from 'i18next';
import { put, call, takeLatest, all, select } from 'redux-saga/effects';
import { processRequest } from '../../services/Api';
import { historyPush, historyReplace } from '../../services/History';
import { groupOverviewActionTypes, CREATE_GROUP_FORM } from './Constants';
import { requestedGroupsCount, invitedGroupsCount, userId } from './Selectors';
import * as groupOverviewActions from './Actions';
import * as sidebarActions from '../../components/sideBar/Actions';
import * as notificationActions from '../../components/notification/Actions';
import * as addImageModalActions from '../../components/modals/addImage/Actions';
import * as errorHandlerActions from '../../components/errorHandler/Actions';
import * as groupDetailsActions from '../groupDetails/Actions';
import * as sharedActions from '../../pagesNext/shared/Actions';

export default function* () {
	yield all([
		yield takeLatest(
			groupOverviewActionTypes.GET_ACTIVE_GROUPS_REQUEST,
			handleActiveGroupsRequest,
		),
		yield takeLatest(
			groupOverviewActionTypes.GET_INVITED_GROUPS_REQUEST,
			handleInvitedGroupsRequest,
		),
		yield takeLatest(
			groupOverviewActionTypes.GET_REQUESTED_GROUPS_REQUEST,
			handleRequestedGroupsRequest,
		),
		yield takeLatest(groupOverviewActionTypes.DELETE_GROUP_REQUEST, handleDeleteGroupRequest),
		yield takeLatest(groupOverviewActionTypes.LEAVE_GROUP_REQUEST, handleLeaveGroupRequest),
		yield takeLatest(
			groupOverviewActionTypes.ACCEPT_INVITATION_REQUEST,
			handleAcceptInvitationRequest,
		),
		yield takeLatest(
			groupOverviewActionTypes.DECLINE_INVITATION_REQUEST,
			handleDeclineInvitationRequest,
		),
		yield takeLatest(
			groupOverviewActionTypes.RETRACT_INVITATION_REQUEST,
			handleRetractInvitationRequest,
		),
		yield takeLatest(groupOverviewActionTypes.JOIN_GROUP_REQUEST, handleJoinGroupRequest),
		yield takeLatest(
			groupOverviewActionTypes.GET_EDUCATION_TYPES_REQUEST,
			handleEducationTypesRequest,
		),
		yield takeLatest(groupOverviewActionTypes.GET_INSTITUTES_REQUEST, handleInstitutesRequest),
		yield takeLatest(groupOverviewActionTypes.GET_SCHOOL_REQUEST, handleGetSchoolRequest),
		yield takeLatest(groupOverviewActionTypes.CREATE_GROUP_REQUEST, handleCreateGroupRequest),
		yield takeLatest(
			groupOverviewActionTypes.GET_AVAILABLE_SUBJECTS_REQUEST,
			handleGetAvailableSubjectsRequest,
		),
		yield takeLatest(
			groupOverviewActionTypes.GET_USER_DETAILS_REQUEST,
			handleGetUserDetailsRequest,
		),
	]);
}

export function* handleActiveGroupsRequest({ payload }) {
	try {
		const { subjectId } = payload || {};

		const { data } = yield call(
			processRequest,
			`groups${subjectId ? `?subject_id=${subjectId}` : ''}`,
		);

		yield put(groupOverviewActions.getActiveGroupsSuccess(data));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(groupOverviewActions.getActiveGroupsError());
	}
}

export function* handleInvitedGroupsRequest() {
	try {
		const { data } = yield call(processRequest, 'groups/invited');

		yield put(groupOverviewActions.getInvitedGroupsSuccess(data));

		if (data.invited_groups_count === 0) {
			yield call(historyPush, '/groups/active');
		}
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(groupOverviewActions.getInvitedGroupsError());
	}
}

export function* handleRequestedGroupsRequest() {
	try {
		const { data } = yield call(processRequest, 'groups/requested');

		yield put(groupOverviewActions.getRequestedGroupsSuccess(data));

		if (data.requested_groups_count === 0) {
			yield call(historyPush, '/groups/active');
		}
	} catch (e) {
		yield put(errorHandlerActions.handleError());
		yield put(groupOverviewActions.getRequestedGroupsError(e));
	}
}

export function* handleDeleteGroupRequest({ payload }) {
	try {
		const { groupId, route } = payload || {};
		yield call(processRequest, `groups/${groupId}`, 'DELETE');

		if (route) {
			yield call(historyReplace, route);
		}
		yield put(sidebarActions.getUseDataRequest());
		yield put(groupOverviewActions.deleteGroupSuccess(groupId));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupOverviewActions.deleteGroupError());
	}
}

export function* handleLeaveGroupRequest({ payload }) {
	try {
		const { groupId, route } = payload || {};
		yield call(processRequest, `groups/${groupId}/leave_group`, 'DELETE');
		if (route) {
			yield call(historyReplace, route);
		}
		yield put(sidebarActions.getUseDataRequest());
		yield put(groupOverviewActions.leaveGroupSuccess(groupId));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupOverviewActions.leaveGroupError(e));
	}
}

export function* handleAcceptInvitationRequest(action) {
	try {
		const { groupId } = action.payload;
		yield call(processRequest, `groups/${groupId}/accept_invite`, 'POST');

		yield put(sidebarActions.getUseDataRequest());
		yield put(groupOverviewActions.acceptInvitationSuccess(groupId));
		yield call(historyPush, `/groups/${groupId}`);
		yield put(
			notificationActions.createNotification(i18n.t('group_overview.accept_invitation')),
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupOverviewActions.acceptInvitationError());
	}
}

export function* handleDeclineInvitationRequest(action) {
	try {
		const { groupId, navigatedFromGroupDetails } = action.payload;
		yield call(processRequest, `groups/${groupId}/reject_invite`, 'DELETE');

		yield put(groupOverviewActions.declineInvitationSuccess(groupId));
		if (navigatedFromGroupDetails) {
			const { data } = yield call(processRequest, `groups/${groupId}`);

			yield put(groupDetailsActions.getGroupDetailsSuccess(data.group));
		} else {
			const groupsCount = yield select(invitedGroupsCount);
			if (groupsCount === 0) {
				yield call(historyReplace, '/groups/active');
			}
		}
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupOverviewActions.declineInvitationError());
	}
}

export function* handleRetractInvitationRequest(action) {
	try {
		const { groupId, navigatedFromGroupDetails } = action.payload;
		yield call(processRequest, `groups/${groupId}/revoke_request`, 'DELETE');

		if (navigatedFromGroupDetails) {
			const { data } = yield call(processRequest, `groups/${groupId}`);
			yield put(groupDetailsActions.getGroupDetailsSuccess(data.group));
		} else {
			yield put(groupOverviewActions.retractInvitationSuccess(groupId));
			const groupsCount = yield select(requestedGroupsCount);
			if (groupsCount === 0) {
				yield call(historyReplace, '/groups/active');
			}
		}
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupOverviewActions.retractInvitationError());
	}
}

export function* handleJoinGroupRequest(action) {
	try {
		const { groupId, navigatedFromGroupDetails, query } = action.payload;
		const requestPayload = {};
		if (query) {
			requestPayload.auto_join_secret = query.key;
		}

		const { data } = yield call(
			processRequest,
			`groups/${groupId}/request_to_join`,
			'POST',
			requestPayload,
		);

		if (navigatedFromGroupDetails) {
			yield put(
				notificationActions.createNotification(
					i18n.t('group_overview.request_to_join_group'),
				),
			);
			const groupDetailsData = yield call(processRequest, `groups/${groupId}`);
			yield put(groupDetailsActions.getGroupDetailsSuccess(groupDetailsData.data.group));
		} else if (!data.success) {
			yield call(historyReplace, '/');
			yield put(notificationActions.createNotification(data.error, null, true));
		} else {
			yield put(groupOverviewActions.joinGroupSuccess());
			yield call(historyReplace, `/groups/${groupId}`);
			yield put(sidebarActions.getUseDataRequest());
			yield put(
				notificationActions.createNotification(
					i18n.t(
						data.status === 'is_group_member_but_not_owner'
							? 'group_overview.request_to_auto_join_group'
							: 'group_overview.request_to_join_group',
					),
				),
			);
		}
	} catch (e) {
		yield put(
			errorHandlerActions.handleError(e, 'notification', {
				code: 404,
				message: i18n.t('group_overview.not_found'),
			}),
		);
		yield put(groupOverviewActions.joinGroupError());
		yield call(historyReplace, '/groups');
	}
}

export function* handleEducationTypesRequest({ payload }) {
	try {
		const { data } = yield call(
			processRequest,
			`countries/${payload.countryId}/education_types`,
		);

		yield put(groupOverviewActions.getEducationTypesSuccess(data));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(groupOverviewActions.getEducationTypesError());
	}
}

export function* handleInstitutesRequest() {
	try {
		const { data } = yield call(processRequest, `institutes`);

		yield put(groupOverviewActions.getInstitutesSuccess(data.institutes));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(groupOverviewActions.getInstitutesError());
	}
}

export function* handleGetSchoolRequest({ payload }) {
	try {
		const { data } = yield call(
			processRequest,
			`countries/${payload.countryId}/schools`,
			'GET',
			{
				q: payload.query,
			},
		);

		yield put(groupOverviewActions.getSchoolsSuccess(data.schools));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(groupOverviewActions.getSchoolsError());
	}
}

export function* handleCreateGroupRequest({ payload }) {
	try {
		const { group, isFromAddListModal } = payload || {};
		const {
			title,
			shared,
			school,
			grade,
			level,
			subject,
			groupId,
			edit,
			schoolName,
			educationType,
			group_image,
			auto_join,
			invitation,
			institute,
		} = group || {};

		const payloadRequest = {
			group: institute
				? {
						name: title,
						subject_id: subject?.id,
						institute_id: institute?.id,
						remote_image_url: group_image,
				  }
				: {
						education_type: educationType,
						name: title,
						shared,
						auto_join,
						remote_image_url: group_image,
						subject_id: subject?.id,
						school_track_id: level?.school_track_id,
						grade_id: grade?.grade_id,
						school_id: school?.id || (school && schoolName?.id),
				  },
		};

		let groupData;

		if (edit) {
			const { data } = yield call(
				processRequest,
				`groups/${groupId}`,
				'PATCH',
				payloadRequest,
			);
			groupData = data;
		} else {
			const { data } = yield call(processRequest, 'groups', 'POST', payloadRequest);
			groupData = data;
		}
		if (isFromAddListModal) yield put(sharedActions.getUserCollections('groups'));
		if (!invitation && !isFromAddListModal) {
			yield call(historyPush, `/groups/${groupData.group.id}`);
		}
		yield put(
			notificationActions.createNotification(
				edit
					? i18n.t('general.saved_changes')
					: i18n.t('group_overview.create_group_notification'),
			),
		);
		yield put(groupOverviewActions.toggleCreateGroupModal(false));
		yield put(
			addImageModalActions.handleAddImageModal({ imageSrc: null, itemId: null, type: null }),
		);
		yield put(groupOverviewActions.resetForm(CREATE_GROUP_FORM));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupOverviewActions.createGroupError());
	}
}

export function* handleGetAvailableSubjectsRequest() {
	try {
		const { data } = yield call(processRequest, 'users/available_subjects');

		yield put(groupOverviewActions.getAvailableSubjectsSuccess(data));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(groupOverviewActions.getAvailableSubjectsError());
	}
}

export function* handleGetUserDetailsRequest() {
	try {
		const id = yield select(userId);
		const { data } = yield call(processRequest, `users/${id}`);

		yield put(groupOverviewActions.getUserDetailsSuccess(data.user));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(groupOverviewActions.getUserDetailsError());
	}
}
